import { mintId, SMS_CONSENT_TARGET } from 'app/lib/forms/builder'
import type { FormQuestion, QuestionKind } from 'app/lib/forms/types'

/** A question inside a block, before ids are minted. `ref` lets a later question in the same
 *  block name the one it depends on, and `showIfRef` + `equals` become its `showWhen`. */
type BlockQuestion = {
  ref: string
  kind: QuestionKind
  label: string
  required: boolean
  helpText?: string
  mappingTarget?: string
  options?: string[]
  showIfRef?: string
  equals?: string
}

export type QuestionBlock = { key: string; name: string; description: string; questions: BlockQuestion[] }

export const QUESTION_BLOCKS: readonly QuestionBlock[] = [
  {
    key: `sms-consent`,
    name: `Text message consent`,
    description: `Yes or no, saved to the contact`,
    questions: [
      {
        ref: `consent`,
        kind: `yesNo`,
        label: `May we send you text messages about your appointments and our office?`,
        helpText: `Message and data rates may apply. Reply STOP at any time to stop texts.`,
        required: true,
        mappingTarget: SMS_CONSENT_TARGET,
      },
    ],
  },
  {
    key: `emergency-contact`,
    name: `Emergency contact`,
    description: `Name, relationship, and phone`,
    questions: [
      { ref: `name`, kind: `shortText`, label: `Emergency contact name`, required: true },
      { ref: `relationship`, kind: `shortText`, label: `Relationship to you`, required: false },
      { ref: `phone`, kind: `phone`, label: `Emergency contact phone`, required: true },
    ],
  },
  {
    key: `responsible-party`,
    name: `Responsible party`,
    description: `Who pays, when it is not the patient`,
    questions: [
      { ref: `self`, kind: `yesNo`, label: `Are you responsible for payment for this visit?`, required: true },
      {
        ref: `name`,
        kind: `shortText`,
        label: `Responsible party name`,
        required: true,
        showIfRef: `self`,
        equals: `no`,
      },
      {
        ref: `relationship`,
        kind: `shortText`,
        label: `Their relationship to the patient`,
        required: true,
        showIfRef: `self`,
        equals: `no`,
      },
      {
        ref: `phone`,
        kind: `phone`,
        label: `Responsible party phone`,
        required: true,
        showIfRef: `self`,
        equals: `no`,
      },
      {
        ref: `birth`,
        kind: `date`,
        label: `Responsible party date of birth`,
        required: false,
        showIfRef: `self`,
        equals: `no`,
      },
    ],
  },
  {
    key: `dental-insurance`,
    name: `Dental insurance`,
    description: `Carrier, subscriber, and member ID`,
    questions: [
      { ref: `has`, kind: `yesNo`, label: `Do you have dental insurance?`, required: true },
      {
        ref: `carrier`,
        kind: `shortText`,
        label: `Insurance company`,
        required: true,
        showIfRef: `has`,
        equals: `yes`,
      },
      {
        ref: `relation`,
        kind: `singleSelect`,
        label: `Who is the insurance subscriber?`,
        required: true,
        options: [`Me`, `My spouse or partner`, `My parent`, `Someone else`],
        showIfRef: `has`,
        equals: `yes`,
      },
      {
        ref: `subscriber`,
        kind: `shortText`,
        label: `Subscriber name`,
        required: true,
        showIfRef: `has`,
        equals: `yes`,
      },
      {
        ref: `subBirth`,
        kind: `date`,
        label: `Subscriber date of birth`,
        required: true,
        showIfRef: `has`,
        equals: `yes`,
      },
      {
        ref: `member`,
        kind: `shortText`,
        label: `Member or subscriber ID`,
        required: true,
        showIfRef: `has`,
        equals: `yes`,
      },
      { ref: `group`, kind: `shortText`, label: `Group number`, required: false, showIfRef: `has`, equals: `yes` },
    ],
  },
  {
    key: `dental-history`,
    name: `Dental history`,
    description: `Last visit, reason, pain, and concerns`,
    questions: [
      { ref: `last`, kind: `date`, label: `When was your last dental visit?`, required: false },
      { ref: `reason`, kind: `longText`, label: `What brings you in?`, required: false },
      { ref: `pain`, kind: `yesNo`, label: `Are you in any dental pain right now?`, required: true },
      { ref: `where`, kind: `longText`, label: `Where is the pain?`, required: true, showIfRef: `pain`, equals: `yes` },
      {
        ref: `issues`,
        kind: `multiSelect`,
        label: `Have you had any of these?`,
        required: false,
        options: [`Bleeding gums`, `Sensitive teeth`, `Jaw pain or clicking`, `Grinding or clenching`, `Dry mouth`],
      },
      { ref: `nervous`, kind: `yesNo`, label: `Do dental visits make you nervous?`, required: false },
    ],
  },
]

/** Mints fresh ids on every use, so the same block can go into a form twice without clashing. */
export function buildBlock(block: QuestionBlock): FormQuestion[] {
  const ids = new Map(block.questions.map((q) => [q.ref, mintId(`q`)]))
  return block.questions.map((q) => {
    const question: FormQuestion = { id: ids.get(q.ref) as string, kind: q.kind, label: q.label, required: q.required }
    if (q.helpText) question.helpText = q.helpText
    if (q.mappingTarget) question.mappingTarget = q.mappingTarget
    if (q.options) question.options = q.options.map((label) => ({ id: mintId(`opt`), label }))
    if (q.showIfRef && q.equals) question.showWhen = { questionId: ids.get(q.showIfRef) as string, equals: q.equals }
    return question
  })
}
